import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Upload, Loader2, CheckCircle2, XCircle, Star, Lock, Unlock,
  Download, Image as ImageIcon, ChevronDown, ChevronUp, ChevronLeft,
  Activity, MessageSquare, Send, Clock,
  Eye, RotateCcw, TrendingUp, Sparkles, SlidersHorizontal, GitBranch, Printer,
  Maximize2, Minimize2, PencilLine, Eraser, Undo2, Redo2, Pin
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { ScrollArea } from '../ui/scroll-area';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import DraggablePivotLayer from './DraggablePivotLayer';
import DoodleCanvas from './DoodleCanvas';
import { buildLedgerRows, hasSyntheticLedgerRows } from '../../lib/confidenceLedger';

export default function AnalysisView({
  project,
  analysis,
  analyzing,
  jobProgress,
  chartView,
  setChartView,
  showFibs,
  setShowFibs,
  showTimeframe,
  setShowTimeframe,
  predictionSteps,
  completionPredictionSteps,
  predictions,
  predictionsLoading,
  expandedPredictions,
  setExpandedPredictions,
  expandedExplain,
  setExpandedExplain,
  expandedFibs,
  setExpandedFibs,
  selectedCandidate,
  setSelectedCandidate,
  selectedCandidateIdx,
  setSelectedCandidateIdx,
  analysisHistory,
  onSelectAnalysis,
  timeframeMethods,
  selectedMethod,
  setSelectedMethod,
  chatMessages,
  chatInput,
  setChatInput,
  chatLoading,
  handleUpload,
  handlePrefer,
  handleExport,
  handleSendChat,
  getChartUrl,
  previewUrl,
  fileRef,
  chatEndRef,
  navigate,
  aiGate,
  chartMetadata,
  onPivotDragEnd,
  mapSourceYToPrice,
  analysisFocus,
  setAnalysisFocus,
  analysisSpan,
  setAnalysisSpan,
  handleReanalyzeScope,
  lockScopeMode,
  setLockScopeMode,
  rescoreBusy,
  strictnessInterval,
  strictnessProbe,
  setStrictnessProbe,
  strictnessSample,
  strictnessPreview,
  strictnessPreviewLoading,
  strictnessApplyBusy,
  strictnessRestoreAvailable,
  manualPresentationMode,
  handleRefreshStrictnessPreview,
  handleRestoreStrictnessStack,
  handleReleaseManualReplot,
  handleApplyStrictnessPreview,
  fractalEnabled,
  setFractalEnabled,
  fractalView,
  fractalPath,
  fractalLoading,
  handleFractalZoom,
  handleFractalBack,
  reportBusy,
  handleReportExport,
  selectedReportSections,
  setSelectedReportSections,
  handleSubmitDoodle,
  doodleSubmitting,
  lastRescoreResult,
  isCurator = false,
  onSetAsLanding,
  setAsLandingBusy = false,
}) {
  const chartImageRef = useRef(null);
  const chartViewportRef = useRef(null);
  const doodleCanvasRef = useRef(null);

  const [imageSize, setImageSize] = useState({
    width: 0,
    height: 0,
    offsetX: 0,
    offsetY: 0,
    naturalWidth: 0,
    naturalHeight: 0,
  });
  const [doodleMode, setDoodleMode] = useState(false);
  const [doodleHasInk, setDoodleHasInk] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [predictionTab, setPredictionTab] = useState('completion');
  const [pivotDrafts, setPivotDrafts] = useState({});
  const [pivotHistory, setPivotHistory] = useState([{}]);
  const [pivotHistoryIndex, setPivotHistoryIndex] = useState(0);
  const [doodleHistory, setDoodleHistory] = useState({ canUndo: false, canRedo: false, hasInk: false });
  const [strictnessDetailOpen, setStrictnessDetailOpen] = useState(false);
  const chartCardRef = useRef(null);
  const chatPanelRef = useRef(null);
  const chartResizeStateRef = useRef(null);

  const [chartPanelHeight, setChartPanelHeight] = useState(() => {
    if (typeof window === 'undefined') return 640;
    const raw = Number(window.localStorage.getItem('wavekit.analysis.chartPanelHeight') || 640);
    return Number.isFinite(raw) ? Math.max(420, Math.min(1400, raw)) : 640;
  });
  const [chatDetached, setChatDetached] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('wavekit.analysis.chatDetached') === 'true';
  });
  const [inlineChatHeight, setInlineChatHeight] = useState(() => {
    if (typeof window === 'undefined') return 320;
    const raw = Number(window.localStorage.getItem('wavekit.analysis.inlineChatHeight') || 320);
    return Number.isFinite(raw) ? Math.max(240, Math.min(760, raw)) : 320;
  });
  const [floatingChatSize, setFloatingChatSize] = useState(() => {
    if (typeof window === 'undefined') return { width: 520, height: 430 };
    const rawW = Number(window.localStorage.getItem('wavekit.analysis.floatingChatWidth') || 520);
    const rawH = Number(window.localStorage.getItem('wavekit.analysis.floatingChatHeight') || 430);
    return {
      width: Number.isFinite(rawW) ? Math.max(360, Math.min(900, rawW)) : 520,
      height: Number.isFinite(rawH) ? Math.max(280, Math.min(860, rawH)) : 430,
    };
  });

  const [spanSelectMode, setSpanSelectMode] = useState(false);
  const [spanBrush, setSpanBrush] = useState(null);
  const [pivotSnapEnabled, setPivotSnapEnabled] = useState(false);
  const [isChartResizing, setIsChartResizing] = useState(false);
  const [showSectionSelector, setShowSectionSelector] = useState(false);

  const normalizedSpan = useMemo(() => {
    const start = Math.min(100, Math.max(0, Number(analysisSpan?.start_pct) || 0));
    const end = Math.min(100, Math.max(0, Number(analysisSpan?.end_pct) || 100));
    const low = Math.min(start, end);
    const high = Math.max(start, end);
    return {
      start_pct: low,
      end_pct: Math.max(low + 1, high),
    };
  }, [analysisSpan]);

  const hasPivotDrafts = Object.keys(pivotDrafts || {}).length > 0;
  const pivotCanUndo = pivotHistoryIndex > 0;
  const pivotCanRedo = pivotHistoryIndex < pivotHistory.length - 1;

  const chartImageClass = isFullscreen
    ? 'w-full h-[calc(100vh-220px)] object-contain'
    : 'w-full h-full object-contain';

  const activeSpan = spanBrush || normalizedSpan;
  const hasImageOverlay = imageSize.width > 0 && imageSize.height > 0;
  const imageOverlayStyle = {
    left: `${imageSize.offsetX || 0}px`,
    top: `${imageSize.offsetY || 0}px`,
    width: `${imageSize.width || 0}px`,
    height: `${imageSize.height || 0}px`,
  };

  const completionPredictions = predictions?.predicted_pivots?.slice(0, Math.max(0, completionPredictionSteps || 0)) || [];
  const hasCompletionPredictions = completionPredictions.length > 0;
  const activePredictedPivots = predictionTab === 'speculative'
    ? (predictions?.predicted_pivots || [])
    : completionPredictions;

  // P2: never invent deltas client-side (removed rule.passed ? 0 : -5 fallback).
  // Backend always populates candidate.ledger from real scoring weights.
  const ledgerRows = buildLedgerRows(selectedCandidate);
  const ledgerHasSynthetic = hasSyntheticLedgerRows(ledgerRows);

  const predictionBiasLabel = useMemo(() => {
    const direction = String(predictions?.direction || '').toLowerCase();
    if (direction === 'bullish') return 'Upside Bias';
    if (direction === 'bearish') return 'Downside Bias';
    return 'Directional Bias';
  }, [predictions?.direction]);

  const strictnessPreviewCandidates = Array.isArray(strictnessPreview?.candidates) ? strictnessPreview.candidates : [];
  const strictnessSpotlight = strictnessPreviewCandidates[0] || null;
  const strictnessToneLabel = (strictnessProbe ?? 0) <= 0.33 ? 'Loose' : (strictnessProbe ?? 0) >= 0.67 ? 'Exact' : 'Balanced';
  const strictnessMatchesSelected = Boolean(
    selectedCandidate
    && strictnessSpotlight
    && String(selectedCandidate?.pattern_type || '').toLowerCase() === String(strictnessSpotlight?.pattern_type || '').toLowerCase()
    && JSON.stringify(selectedCandidate?.labels || []) === JSON.stringify(strictnessSpotlight?.labels || [])
  );

  const historyItems = useMemo(() => (Array.isArray(analysisHistory) ? [...analysisHistory] : []).sort((left, right) => {
    const leftTime = Date.parse(left?.last_rescored_at || left?.created_at || 0) || 0;
    const rightTime = Date.parse(right?.last_rescored_at || right?.created_at || 0) || 0;
    return rightTime - leftTime;
  }), [analysisHistory]);

  const clampChartPanelHeight = useCallback((value) => Math.max(420, Math.min(1400, Math.round(Number(value) || 640))), []);

  const beginChartResize = useCallback((event) => {
    if (isFullscreen) return;
    event.preventDefault();
    chartResizeStateRef.current = {
      startY: event.clientY,
      startHeight: chartPanelHeight,
    };
    setIsChartResizing(true);
  }, [chartPanelHeight, isFullscreen]);

  const syncImageSize = () => {
    const img = chartImageRef.current;
    const container = img?.parentElement;
    if (!img || !container) return;

    const naturalWidth = img.naturalWidth || img.clientWidth || 0;
    const naturalHeight = img.naturalHeight || img.clientHeight || 0;
    const containerWidth = container.clientWidth || img.clientWidth || 0;
    const containerHeight = container.clientHeight || img.clientHeight || 0;

    let width = img.clientWidth || 0;
    let height = img.clientHeight || 0;
    let offsetX = 0;
    let offsetY = 0;

    if (naturalWidth > 0 && naturalHeight > 0 && containerWidth > 0 && containerHeight > 0) {
      const scale = Math.min(containerWidth / naturalWidth, containerHeight / naturalHeight);
      width = Math.max(1, Math.round(naturalWidth * scale));
      height = Math.max(1, Math.round(naturalHeight * scale));
      offsetX = Math.max(0, Math.round((containerWidth - width) / 2));
      offsetY = Math.max(0, Math.round((containerHeight - height) / 2));
    }

    setImageSize({
      width,
      height,
      offsetX,
      offsetY,
      naturalWidth,
      naturalHeight,
    });
  };

  useEffect(() => {
    syncImageSize();
    const onResize = () => syncImageSize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [chartView, selectedCandidateIdx, showFibs, showTimeframe, predictionSteps, analysis?.id, isFullscreen]);

  useEffect(() => {
    if (hasCompletionPredictions) {
      setPredictionTab('completion');
    } else {
      setPredictionTab('speculative');
    }
  }, [selectedCandidate?.id, completionPredictionSteps, hasCompletionPredictions]);

  useEffect(() => {
    const onFullscreenChange = () => {
      const fullscreenEl = document.fullscreenElement;
      setIsFullscreen(Boolean(fullscreenEl && chartViewportRef.current && fullscreenEl === chartViewportRef.current));
      setTimeout(syncImageSize, 30);
    };

    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('wavekit.analysis.chartPanelHeight', String(Math.round(chartPanelHeight)));
  }, [chartPanelHeight]);

  useEffect(() => {
    if (!isChartResizing) return undefined;

    const handlePointerMove = (event) => {
      const session = chartResizeStateRef.current;
      if (!session) return;
      const nextHeight = session.startHeight + (event.clientY - session.startY);
      setChartPanelHeight(clampChartPanelHeight(nextHeight));
    };

    const stopResize = () => {
      chartResizeStateRef.current = null;
      setIsChartResizing(false);
    };

    const previousCursor = document.body.style.cursor;
    const previousSelect = document.body.style.userSelect;
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', stopResize);
    window.addEventListener('pointercancel', stopResize);

    return () => {
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousSelect;
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', stopResize);
      window.removeEventListener('pointercancel', stopResize);
    };
  }, [clampChartPanelHeight, isChartResizing]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('wavekit.analysis.chatDetached', String(chatDetached));
  }, [chatDetached]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('wavekit.analysis.inlineChatHeight', String(Math.round(inlineChatHeight)));
  }, [inlineChatHeight]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('wavekit.analysis.floatingChatWidth', String(Math.round(floatingChatSize.width)));
    window.localStorage.setItem('wavekit.analysis.floatingChatHeight', String(Math.round(floatingChatSize.height)));
  }, [floatingChatSize]);

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;
    const observers = [];

    if (chartCardRef.current) {
      const chartObserver = new ResizeObserver((entries) => {
        const rect = entries?.[0]?.contentRect;
        const nextHeight = Number(rect?.height || 0);
        if (nextHeight > 0 && !isFullscreen) {
          setChartPanelHeight((prev) => {
            if (Math.abs(prev - nextHeight) < 2) return prev;
            return Math.max(420, Math.min(1400, Math.round(nextHeight)));
          });
        }
        syncImageSize();
      });
      chartObserver.observe(chartCardRef.current);
      observers.push(chartObserver);
    }

    if (chatPanelRef.current) {
      const chatObserver = new ResizeObserver((entries) => {
        const rect = entries?.[0]?.contentRect;
        const nextWidth = Number(rect?.width || 0);
        const nextHeight = Number(rect?.height || 0);

        if (chatDetached) {
          if (nextWidth > 0 || nextHeight > 0) {
            setFloatingChatSize((prev) => ({
              width: nextWidth > 0 ? Math.max(360, Math.min(900, Math.round(nextWidth))) : prev.width,
              height: nextHeight > 0 ? Math.max(280, Math.min(860, Math.round(nextHeight))) : prev.height,
            }));
          }
        } else if (nextHeight > 0) {
          setInlineChatHeight((prev) => {
            if (Math.abs(prev - nextHeight) < 2) return prev;
            return Math.max(240, Math.min(760, Math.round(nextHeight)));
          });
        }
      });
      chatObserver.observe(chatPanelRef.current);
      observers.push(chatObserver);
    }

    return () => observers.forEach((observer) => observer.disconnect());
  }, [chatDetached, isFullscreen]);

  const applySpanFromDrag = (startPct, endPct) => {
    const low = Math.max(0, Math.min(100, Math.min(startPct, endPct)));
    const high = Math.max(0, Math.min(100, Math.max(startPct, endPct)));
    const next = {
      start_pct: Math.round(low * 10) / 10,
      end_pct: Math.round(Math.max(low + 1, high) * 10) / 10,
    };
    setAnalysisSpan?.(next);
  };

  const handleSpanPointerDown = (event) => {
    if (!spanSelectMode) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const pct = ((event.clientX - rect.left) / Math.max(1, rect.width)) * 100;
    const clamped = Math.max(0, Math.min(100, pct));
    setSpanBrush({
      start_pct: clamped,
      end_pct: clamped,
      pointerId: event.pointerId,
    });
    if (event.currentTarget.setPointerCapture) {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
  };

  const handleSpanPointerMove = (event) => {
    if (!spanBrush || spanBrush.pointerId !== event.pointerId) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const pct = ((event.clientX - rect.left) / Math.max(1, rect.width)) * 100;
    const clamped = Math.max(0, Math.min(100, pct));
    setSpanBrush((prev) => prev ? { ...prev, end_pct: clamped } : prev);
  };

  const handleSpanPointerUp = (event) => {
    if (!spanBrush || spanBrush.pointerId !== event.pointerId) return;
    applySpanFromDrag(spanBrush.start_pct, spanBrush.end_pct);
    setSpanBrush(null);
  };

  const handleScopeReset = () => {
    setSpanBrush(null);
    setAnalysisSpan?.({ start_pct: 0, end_pct: 100 });
  };

  const handleScopeApply = async () => {
    if (!handleReanalyzeScope) return;
    await handleReanalyzeScope();
  };

  const chatBodyHeight = chatDetached
    ? Math.max(160, floatingChatSize.height - 150)
    : Math.max(160, inlineChatHeight - 150);

  const chatPanelContent = (
    <>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-sm font-medium text-white flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-[#f5b400]" /> Chat with AI
            <span className="text-[10px] text-[#a1a1aa] font-normal">Discuss the analysis, ask questions, or request reassessment</span>
          </CardTitle>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setChatDetached((prev) => !prev)}
            className="h-7 px-2 text-[10px] border-white/10 bg-[#14161a] hover:bg-white/5"
          >
            {chatDetached ? 'Attach' : 'Pop Out'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <ScrollArea className="mb-3" style={{ height: `${chatBodyHeight}px` }}>
          <div className="space-y-3 pr-2" data-testid="chat-messages">
            {chatMessages.length === 0 && (
              <div className="text-center py-8">
                <MessageSquare className="w-8 h-8 text-[#a1a1aa]/20 mx-auto mb-2" />
                <p className="text-xs text-[#a1a1aa]">Ask about the wave count, request explanations, or discuss alternative interpretations</p>
              </div>
            )}
            {chatMessages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-sm px-3 py-2 text-xs leading-relaxed ${m.role === 'user'
                    ? 'bg-[#f5b400]/10 text-white border border-[#f5b400]/20'
                    : 'bg-white/[0.03] text-[#a1a1aa] border border-white/5'
                  }`}>
                  <pre className="whitespace-pre-wrap font-[inherit]">{m.content}</pre>
                </div>
              </div>
            ))}
            {chatLoading && (
              <div className="flex justify-start">
                <div className="bg-white/[0.03] border border-white/5 rounded-sm px-3 py-2">
                  <Loader2 className="w-4 h-4 animate-spin text-[#f5b400]" />
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
        </ScrollArea>
        <div className="flex gap-2">
          <Input
            data-testid="chat-input"
            value={chatInput}
            onChange={e => setChatInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSendChat()}
            placeholder="Ask about this wave count..."
            className="bg-[#0e0f12] border-white/10 focus:border-[#f5b400] rounded-sm text-sm flex-1"
            disabled={chatLoading}
          />
          <Button
            data-testid="chat-send-btn"
            onClick={handleSendChat}
            disabled={chatLoading || !chatInput.trim()}
            className="bg-[#f5b400] text-black hover:bg-[#d9a000] rounded-sm"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </>
  );
  const toggleFullscreen = async () => {
    if (!chartViewportRef.current) return;

    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await chartViewportRef.current.requestFullscreen();
      }
      setTimeout(syncImageSize, 60);
    } catch (error) {
      console.error('Fullscreen toggle failed:', error);
    }
  };

  const clearDoodles = () => {
    doodleCanvasRef.current?.clear?.();
    setDoodleHasInk(false);
  };

  const submitDoodlesForAnalysis = async () => {
    if (!handleSubmitDoodle || !chartImageRef.current || !doodleCanvasRef.current) return;
    if (!doodleCanvasRef.current.hasInk?.()) return;

    const sourceImg = chartImageRef.current;
    const doodleCanvas = doodleCanvasRef.current.getCanvasElement?.();
    if (!sourceImg || !doodleCanvas) return;

    const mergedCanvas = document.createElement('canvas');
    const naturalW = sourceImg.naturalWidth || sourceImg.clientWidth || imageSize.width || 1;
    const naturalH = sourceImg.naturalHeight || sourceImg.clientHeight || imageSize.height || 1;

    mergedCanvas.width = naturalW;
    mergedCanvas.height = naturalH;

    const ctx = mergedCanvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(sourceImg, 0, 0, naturalW, naturalH);
    ctx.drawImage(doodleCanvas, 0, 0, naturalW, naturalH);

    const userPivots = doodleCanvasRef.current.getUserPivots?.() || [];

    await new Promise((resolve) => {
      mergedCanvas.toBlob(async (blob) => {
        if (blob) {
          await handleSubmitDoodle(blob, userPivots);
          clearDoodles();
          setDoodleMode(false);
        }
        resolve();
      }, 'image/png');
    });
  };

  const resetPivotDrafts = () => {
    setPivotDrafts({});
    setPivotHistory([{}]);
    setPivotHistoryIndex(0);
  };

  const handlePivotDraftCommit = (nextDrafts) => {
    const normalized = nextDrafts || {};
    setPivotDrafts(normalized);

    setPivotHistory((prev) => {
      const head = prev.slice(0, pivotHistoryIndex + 1);
      const last = head[head.length - 1] || {};
      if (JSON.stringify(last) === JSON.stringify(normalized)) return prev;
      const merged = [...head, normalized];
      setPivotHistoryIndex(merged.length - 1);
      return merged;
    });
  };

  const handlePivotUndo = useCallback(() => {
    if (!pivotCanUndo) return;
    const nextIdx = pivotHistoryIndex - 1;
    setPivotHistoryIndex(nextIdx);
    setPivotDrafts(pivotHistory[nextIdx] || {});
  }, [pivotCanUndo, pivotHistory, pivotHistoryIndex]);

  const handlePivotRedo = useCallback(() => {
    if (!pivotCanRedo) return;
    const nextIdx = pivotHistoryIndex + 1;
    setPivotHistoryIndex(nextIdx);
    setPivotDrafts(pivotHistory[nextIdx] || {});
  }, [pivotCanRedo, pivotHistory, pivotHistoryIndex]);

  const handleApplyPivotDrafts = async () => {
    if (!hasPivotDrafts || !onPivotDragEnd || rescoreBusy) return;
    try {
      await onPivotDragEnd(pivotDrafts);
      resetPivotDrafts();
    } catch (error) {
      // parent handles API failures
    }
  };

  useEffect(() => {
    resetPivotDrafts();
  }, [analysis?.id, selectedCandidate?.id, chartMetadata?.candidate_id]);

  useEffect(() => {
    const onKeyDown = (event) => {
      const target = event.target;
      const tagName = String(target?.tagName || '').toLowerCase();
      if (tagName === 'input' || tagName === 'textarea' || target?.isContentEditable) return;

      const key = String(event.key || '').toLowerCase();
      const wantsUndo = event.ctrlKey && !event.shiftKey && key === 'z';
      const wantsRedo = (event.ctrlKey && key === 'y') || (event.ctrlKey && event.shiftKey && key === 'z');
      if (!wantsUndo && !wantsRedo) return;

      event.preventDefault();

      if (wantsUndo) {
        if (doodleMode && doodleCanvasRef.current?.canUndo?.()) {
          doodleCanvasRef.current.undo?.();
          return;
        }
        if (pivotCanUndo) {
          handlePivotUndo();
        }
        return;
      }

      if (doodleMode && doodleCanvasRef.current?.canRedo?.()) {
        doodleCanvasRef.current.redo?.();
        return;
      }
      if (pivotCanRedo) {
        handlePivotRedo();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [doodleMode, pivotCanUndo, pivotCanRedo, pivotHistory, pivotHistoryIndex, handlePivotUndo, handlePivotRedo]);

  if (analyzing) {
    return (
      <Card className="bg-[#14161a] border-white/5">
        <CardContent className="flex flex-col items-center justify-center h-[400px] gap-4">
          <Loader2 className="w-10 h-10 animate-spin text-[#f5b400]" />
          <p className="text-sm text-[#a1a1aa]">Analyzing chart image...</p>
          <div className="w-64"><Progress value={jobProgress} className="h-2" /></div>
          <p className="text-xs font-mono text-[#a1a1aa]">{jobProgress}%</p>
        </CardContent>
      </Card>
    );
  }

  if (!analysis) {
    return (
      <Card className="bg-[#14161a] border-white/5">
        <CardContent className="p-0">
          <div className="wk-upload-zone flex flex-col items-center justify-center h-[400px] m-4 gap-4" onClick={() => fileRef.current?.click()} data-testid="upload-zone">
            <Upload className="w-10 h-10 text-[#a1a1aa]/50" />
            <p className="text-sm text-[#a1a1aa]">Drop a chart image or click to upload</p>
            <p className="text-xs text-[#a1a1aa]/50">PNG, JPG, WEBP — Max 10MB</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {aiGate?.required && aiGate?.checked && !aiGate?.ready && (
        <Card className="bg-[#2a120f] border-[#ff3b30]/30">
          <CardContent className="py-3">
            <p className="text-xs text-[#ffb4ad]">
              AI vision is required and no {aiGate.provider === 'google' ? 'Google AI' : 'OpenRouter'} key is configured.
              Open Settings and add a key before uploading.
            </p>
          </CardContent>
        </Card>
      )}
      {/* Chart Views + Candidates */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: Dual Chart View */}
        <div ref={chartViewportRef} className={`lg:col-span-2 space-y-3 ${isFullscreen ? 'bg-[#0b0d10] h-screen overflow-auto p-4 rounded-sm' : ''}`}>
          {/* View Controls */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-1 bg-[#14161a] rounded-sm p-1 border border-white/5">
              {[
                { id: 'annotated', label: 'Annotated', icon: Eye },
                { id: 'clean', label: 'Clean Replot', icon: RotateCcw },
                { id: 'original', label: 'Original', icon: ImageIcon },
              ].map(v => (
                <button
                  key={v.id}
                  data-testid={`view-${v.id}`}
                  onClick={() => setChartView(v.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-medium transition-colors ${chartView === v.id ? 'bg-[#f5b400]/10 text-[#f5b400]' : 'text-[#a1a1aa] hover:text-white'
                    }`}
                >
                  <v.icon className="w-3 h-3" /> {v.label}
                </button>
              ))}
              {isCurator && analysis?.id && (
                <button
                  type="button"
                  data-testid="set-as-landing-btn"
                  onClick={() => onSetAsLanding?.()}
                  disabled={setAsLandingBusy}
                  title="Pin this analysis and chart view as the site landing for all visitors"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-medium border border-[#f5b400]/40 text-[#f5b400] hover:bg-[#f5b400]/10 transition-colors disabled:opacity-50"
                >
                  {setAsLandingBusy ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Pin className="w-3 h-3" />
                  )}
                  Set as Landing
                </button>
              )}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-1.5 bg-[#14161a] rounded-sm px-2 py-1 border border-white/5" data-testid="prediction-controls">
                <TrendingUp className="w-3 h-3 text-[#00f5d4]" />
                <span className="text-[10px] text-[#a1a1aa] uppercase tracking-wider">Auto Predict</span>
                <span className="font-mono text-xs text-[#00f5d4]">to structure end + speculative</span>
                {predictionsLoading && <Loader2 className="w-3 h-3 animate-spin text-[#00f5d4]" />}
              </div>

              <div className="flex items-center gap-1 rounded-sm px-1 py-1 border border-white/5 bg-[#14161a]" data-testid="focus-toggle">
                <span className="text-[10px] uppercase tracking-wider text-[#a1a1aa] px-1">Focus</span>
                {['macro', 'intermediate', 'micro'].map((focusKey) => (
                  <button
                    key={focusKey}
                    type="button"
                    onClick={() => setAnalysisFocus?.(focusKey)}
                    className={`px-2 py-1 text-[10px] rounded-sm font-mono transition-colors ${analysisFocus === focusKey ? 'bg-[#f5b400]/15 text-[#f5b400]' : 'text-[#a1a1aa] hover:text-white'}`}
                  >
                    {focusKey === 'intermediate' ? 'mid' : focusKey}
                  </button>
                ))}
              </div>

              {!isFullscreen && (
                <div className="text-[10px] text-[#6b7280] font-mono px-1">Drag the lower edge of the chart panel to resize</div>
              )}

              <div className="flex items-center gap-1 rounded-sm px-1 py-1 border border-white/5 bg-[#14161a]">
                <span className="text-[10px] uppercase tracking-wider text-[#a1a1aa] px-1">Scope</span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSpanSelectMode((prev) => !prev)}
                  className={`h-7 px-2 text-[10px] ${spanSelectMode ? 'text-[#00f5d4]' : 'text-[#a1a1aa]'}`}
                >
                  {spanSelectMode ? 'Selecting' : 'Select'}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleScopeReset}
                  className="h-7 px-2 text-[10px]"
                >
                  Full
                </Button>
                <Button
                  size="sm"
                  onClick={handleScopeApply}
                  disabled={analyzing || rescoreBusy}
                  className="h-7 px-2 text-[10px] bg-[#00f5d4]/20 text-[#00f5d4] border border-[#00f5d4]/30 hover:bg-[#00f5d4]/25"
                >
                  Reanalyze
                </Button>
                <span className="text-[10px] font-mono text-[#a1a1aa] px-1">
                  {Math.round(activeSpan.start_pct)}-{Math.round(activeSpan.end_pct)}%
                </span>
              </div>

              <div className="flex items-center gap-1 rounded-sm px-1 py-1 border border-white/5 bg-[#14161a]">
                <span className="text-[10px] uppercase tracking-wider text-[#a1a1aa] px-1">Lock Scope</span>
                <Switch checked={!!lockScopeMode} onCheckedChange={setLockScopeMode} />
              </div>

              <div className="flex items-center gap-1 rounded-sm px-1 py-1 border border-white/5 bg-[#14161a]">
                <span className="text-[10px] uppercase tracking-wider text-[#a1a1aa] px-1">Grid Guide</span>
                <Switch checked={pivotSnapEnabled} onCheckedChange={setPivotSnapEnabled} />
              </div>

              <Button
                size="sm"
                variant="outline"
                onClick={() => setDoodleMode((prev) => !prev)}
                className={`h-7 px-2 text-[10px] border-white/10 bg-[#14161a] hover:bg-white/5 ${doodleMode ? 'text-[#f5b400]' : 'text-[#a1a1aa]'}`}
              >
                <PencilLine className="w-3 h-3 mr-1" /> {doodleMode ? 'Exit Doodle' : 'Doodle'}
              </Button>

              {doodleMode && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => doodleCanvasRef.current?.undo?.()}
                    disabled={!doodleHistory.canUndo}
                    className="h-7 px-2 text-[10px] border-white/10 bg-[#14161a] hover:bg-white/5"
                  >
                    <Undo2 className="w-3 h-3 mr-1" /> Undo
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => doodleCanvasRef.current?.redo?.()}
                    disabled={!doodleHistory.canRedo}
                    className="h-7 px-2 text-[10px] border-white/10 bg-[#14161a] hover:bg-white/5"
                  >
                    <Redo2 className="w-3 h-3 mr-1" /> Redo
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={clearDoodles}
                    className="h-7 px-2 text-[10px] border-white/10 bg-[#14161a] hover:bg-white/5"
                  >
                    <Eraser className="w-3 h-3 mr-1" /> Clear
                  </Button>
                  <Button
                    size="sm"
                    onClick={submitDoodlesForAnalysis}
                    disabled={!doodleHasInk || doodleSubmitting}
                    className="h-7 px-2 text-[10px] bg-[#00f5d4]/20 text-[#00f5d4] border border-[#00f5d4]/30 hover:bg-[#00f5d4]/25"
                  >
                    {doodleSubmitting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                    Submit Doodle
                  </Button>
                </>
              )}

              <Button
                size="sm"
                variant="outline"
                onClick={toggleFullscreen}
                className="h-7 px-2 text-[10px] border-white/10 bg-[#14161a] hover:bg-white/5"
              >
                {isFullscreen ? <Minimize2 className="w-3 h-3 mr-1" /> : <Maximize2 className="w-3 h-3 mr-1" />} {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
              </Button>

              <div className="flex items-center gap-1 rounded-sm px-1 py-1 border border-white/10 bg-[#14161a]">
                <span className={`text-[10px] uppercase tracking-wider px-1 ${hasPivotDrafts ? 'text-[#f5b400]' : 'text-[#a1a1aa]'}`}>Draft {hasPivotDrafts ? `${Object.keys(pivotDrafts).length}` : '0'}</span>
                <Button size="sm" variant="ghost" onClick={handlePivotUndo} disabled={!pivotCanUndo || rescoreBusy} className="h-7 px-2 text-[10px]">
                  <Undo2 className="w-3 h-3 mr-1" /> Undo
                </Button>
                <Button size="sm" variant="ghost" onClick={handlePivotRedo} disabled={!pivotCanRedo || rescoreBusy} className="h-7 px-2 text-[10px]">
                  <Redo2 className="w-3 h-3 mr-1" /> Redo
                </Button>
                <Button size="sm" variant="ghost" onClick={resetPivotDrafts} disabled={!hasPivotDrafts || rescoreBusy} className="h-7 px-2 text-[10px]">
                  Reset
                </Button>
                <Button size="sm" onClick={handleApplyPivotDrafts} disabled={!hasPivotDrafts || rescoreBusy || doodleMode} className="h-7 px-2 text-[10px] bg-[#00f5d4]/20 text-[#00f5d4] border border-[#00f5d4]/30 hover:bg-[#00f5d4]/25">
                  {rescoreBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />} Apply/Rescore
                </Button>
              </div>

              {manualPresentationMode && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleReleaseManualReplot}
                  className="h-7 px-2 text-[10px] border-[#f5b400]/30 bg-[#f5b400]/10 text-[#f5b400] hover:bg-[#f5b400]/15"
                >
                  <RotateCcw className="w-3 h-3 mr-1" /> Release Manual Replot
                </Button>
              )}

              <div className="flex items-center gap-1.5 relative">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setShowSectionSelector(!showSectionSelector)}
                                className={`h-7 px-2 text-[10px] border-white/10 ${showSectionSelector ? 'bg-[#f5b400]/20 text-[#f5b400]' : 'bg-[#14161a] text-[#a1a1aa]'} hover:bg-white/5`}
                                title="Select Report Sections"
                              >
                                <Activity className="w-3 h-3 mr-1" /> Sections
                              </Button>

                              {showSectionSelector && (
                                <div className="absolute bottom-full mb-2 right-0 z-50 w-64 glass-panel p-3 border border-white/10 animate-in fade-in zoom-in-95 duration-200 shadow-2xl">
                                  <div className="flex items-center justify-between mb-2">
                                     <span className="text-[10px] font-bold uppercase tracking-widest text-[#a1a1aa]">Export Segments</span>
                                     <button 
                                        onClick={() => setSelectedReportSections?.(["Executive Summary", "Visual Evidence", "Wave Analysis", "Wall Street Narrative", "Indicators", "Confidence Ledger", "Fibonacci Matrix", "Time Projections", "Divergences", "Wave Degree", "Risk/Reward", "Confluence", "Sentiment", "Fractal Analysis", "Heuristics", "Methodology"])}
                                        className="text-[9px] text-[#f5b400] hover:underline"
                                     >
                                       Select All
                                     </button>
                                  </div>
                                  <div className="grid grid-cols-1 gap-1 max-h-48 overflow-y-auto pr-1 text-left">
                                     {[
                                       "Executive Summary", "Visual Evidence", "Wave Analysis", "Wall Street Narrative",
                                       "Indicators", "Confidence Ledger", "Fibonacci Matrix", "Time Projections", "Divergences",
                                       "Wave Degree", "Risk/Reward", "Confluence", "Sentiment", "Fractal Analysis",
                                       "Heuristics", "Methodology"
                                     ].map(section => (
                                       <label key={section} className="flex items-center justify-between p-2 rounded hover:bg-white/5 cursor-pointer group">
                                         <span className="text-[11px] text-[#a1a1aa] group-hover:text-white transition-colors">{section}</span>
                                         <Switch
                                           checked={selectedReportSections?.includes(section)}
                                           onCheckedChange={(checked) => {
                                             if (checked) {
                                                setSelectedReportSections?.([...(selectedReportSections || []), section]);
                                             } else {
                                                setSelectedReportSections?.(selectedReportSections?.filter(s => s !== section) || []);
                                             }
                                           }}
                                           className="scale-75 origin-right"
                                         />
                                       </label>
                                     ))}
                                  </div>
                                </div>
                              )}

                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleReportExport?.('html')}
                                disabled={reportBusy || !analysis?.id}
                                className="h-7 px-2 text-[10px] border-white/10 bg-[#14161a] hover:bg-white/5"
                              >
                                {reportBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Printer className="w-3 h-3 mr-1" />} Report HTML
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleReportExport?.('pdf')}
                                disabled={reportBusy || !analysis?.id}
                                className="h-7 px-2 text-[10px] border-white/10 bg-[#14161a] hover:bg-white/5"
                              >
                                {reportBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Download className="w-3 h-3 mr-1" />} Print PDF
                              </Button>
            </div>

              <label className="flex items-center gap-1.5 text-xs text-[#a1a1aa] cursor-pointer">
                <Switch checked={showFibs} onCheckedChange={setShowFibs} data-testid="toggle-fibs" />
                Fibs
              </label>
              <label className="flex items-center gap-1.5 text-xs text-[#a1a1aa] cursor-pointer">
                <Switch checked={showTimeframe} onCheckedChange={setShowTimeframe} data-testid="toggle-timeframe" />
                <Clock className="w-3 h-3" /> Time Est.
              </label>
            </div>
          </div>
          {false && strictnessInterval && (
            <Card className="overflow-hidden border-[#00f5d4]/15 bg-[radial-gradient(circle_at_top_left,_rgba(0,245,212,0.14),_transparent_45%),linear-gradient(180deg,_rgba(20,22,26,0.98),_rgba(11,13,16,0.98))]" data-testid="strictness-panel">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <CardTitle className="text-sm font-medium text-white flex items-center gap-2">
                      <SlidersHorizontal className="w-4 h-4 text-[#00f5d4]" /> Strictness Spotlight
                    </CardTitle>
                    <p className="mt-1 text-xs text-[#8b93a7]">
                      Slide from loose to exact and preview which Elliott count deserves the stage before you promote it.
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap text-[10px] font-mono">
                    <span className="rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 text-[#a1a1aa]">
                      Best {(strictnessInterval.best?.strictness ?? 0).toFixed(2)}
                    </span>
                    <span className="rounded-full border border-[#f5b400]/20 bg-[#f5b400]/10 px-2.5 py-1 text-[#f5b400]">
                      {strictnessToneLabel}
                    </span>
                    <span className="rounded-full border border-[#00f5d4]/20 bg-[#00f5d4]/10 px-2.5 py-1 text-[#00f5d4]">
                      {strictnessPreviewCandidates.length} preview
                    </span>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 xl:grid-cols-4 gap-2 text-xs">
                  <div className="rounded-sm border border-white/5 bg-white/[0.03] p-3">
                    <p className="text-[#8b93a7]">Probe</p>
                    <p className="mt-1 font-mono text-lg font-bold text-[#f5b400]">{(strictnessProbe ?? 0).toFixed(2)}</p>
                  </div>
                  <div className="rounded-sm border border-white/5 bg-white/[0.03] p-3">
                    <p className="text-[#8b93a7]">Best Interval</p>
                    <p className="mt-1 font-mono text-sm font-bold text-white">
                      {(strictnessInterval.best_interval?.low ?? 0).toFixed(2)} - {(strictnessInterval.best_interval?.high ?? 0).toFixed(2)}
                    </p>
                  </div>
                  <div className="rounded-sm border border-white/5 bg-white/[0.03] p-3">
                    <p className="text-[#8b93a7]">Map Sample</p>
                    <p className="mt-1 text-sm font-bold uppercase text-[#00f5d4]">{String(strictnessSample?.pattern_type || 'pending')}</p>
                    <p className="font-mono text-[11px] text-[#a1a1aa]">{(strictnessSample?.confidence ?? 0).toFixed(1)}%</p>
                  </div>
                  <div className="rounded-sm border border-white/5 bg-white/[0.03] p-3">
                    <p className="text-[#8b93a7]">Preview Leader</p>
                    <p className="mt-1 text-sm font-bold uppercase text-white">{String(strictnessSpotlight?.pattern_type || 'pending')}</p>
                    <p className="font-mono text-[11px] text-[#a1a1aa]">{(strictnessSpotlight?.confidence ?? 0).toFixed(1)}%</p>
                  </div>
                </div>

                <div className="rounded-xl border border-white/8 bg-black/20 p-3">
                  <div className="mb-2 flex items-center justify-between text-[11px] text-[#a1a1aa]">
                    <span>Loose</span>
                    <span className="font-mono text-[#f5b400]">Probe {(strictnessProbe ?? 0).toFixed(2)}</span>
                    <span>Exact</span>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={1}
                    value={Math.round((strictnessProbe ?? 0) * 100)}
                    onChange={(event) => setStrictnessProbe?.(Number(event.target.value) / 100)}
                    className="w-full accent-[#f5b400]"
                  />
                  <div className="mt-2 flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-[#6b7280]">
                    <span>Fluff</span>
                    <span>Balance</span>
                    <span>Discipline</span>
                  </div>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-[1.55fr_1fr] gap-3">
                  <div className="rounded-xl border border-[#00f5d4]/15 bg-[#00f5d4]/[0.06] p-4 min-h-[176px]">
                    {strictnessPreviewLoading ? (
                      <div className="h-full min-h-[140px] flex items-center justify-center gap-2 text-sm text-[#8b93a7]">
                        <Loader2 className="w-4 h-4 animate-spin text-[#00f5d4]" /> Reading the new count stack...
                      </div>
                    ) : strictnessSpotlight ? (
                      <div className="space-y-3">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div>
                            <p className="text-[11px] uppercase tracking-[0.24em] text-[#8b93a7]">Spotlight Count</p>
                            <h3 className="mt-1 text-xl font-semibold text-white uppercase">{String(strictnessSpotlight.pattern_type || 'unknown')}</h3>
                          </div>
                          <div className="text-right">
                            <p className="text-[11px] uppercase tracking-[0.24em] text-[#8b93a7]">Confidence</p>
                            <p className="font-mono text-2xl font-bold text-[#00f5d4]">{(strictnessSpotlight.confidence ?? 0).toFixed(1)}%</p>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {(strictnessSpotlight.labels || []).map((label, idx) => (
                            <span key={`${label}-${idx}`} className="rounded-full border border-white/10 bg-black/20 px-2.5 py-1 text-[11px] font-mono text-white">
                              {label}
                            </span>
                          ))}
                        </div>
                        <p className="text-sm leading-relaxed text-[#d4d7dd]">
                          {strictnessSpotlight.rationale || 'This strictness setting is surfacing the cleanest active interpretation from the current pivot sequence.'}
                        </p>
                        <p className="text-[11px] text-[#8b93a7]">
                          {strictnessMatchesSelected ? 'This spotlight already matches the active saved count.' : 'Apply it to push this interpretation into the live candidate stack and chart workflow.'}
                        </p>
                      </div>
                    ) : (
                      <div className="h-full min-h-[140px] flex items-center justify-center text-sm text-[#8b93a7]">
                        No alternate counts surfaced for this probe yet.
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.24em] text-[#8b93a7]">
                      <span>Preview Stack</span>
                      <span className="font-mono normal-case tracking-normal text-[#a1a1aa]">{strictnessPreviewCandidates.length} candidates</span>
                    </div>
                    {strictnessPreviewCandidates.slice(0, 3).map((candidate, idx) => (
                      <div key={`strictness-preview-${idx}`} className={`rounded-sm border px-3 py-2 ${idx === 0 ? 'border-[#00f5d4]/25 bg-[#00f5d4]/10' : 'border-white/5 bg-white/[0.03]'}`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-semibold uppercase text-white">{String(candidate.pattern_type || 'unknown')}</span>
                          <span className="font-mono text-[11px] text-[#f5b400]">{(candidate.confidence ?? 0).toFixed(1)}%</span>
                        </div>
                        <p className="mt-1 text-[11px] font-mono text-[#8b93a7]">{(candidate.labels || []).join(' → ') || 'No labels'}</p>
                      </div>
                    ))}
                    {!strictnessPreviewCandidates.length && !strictnessPreviewLoading && (
                      <div className="rounded-sm border border-dashed border-white/10 p-3 text-xs text-[#6b7280]">
                        The current probe did not surface a stronger alternate stack.
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <p className="text-[11px] text-[#8b93a7] max-w-2xl">
                    Preview runs on the current pivot sequence. Apply it to reorder the live candidate stack and put that interpretation front and center.
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setStrictnessProbe?.(strictnessInterval.best?.strictness ?? strictnessProbe)}
                      className="h-8 px-3 text-[11px] border-white/10 bg-[#14161a] hover:bg-white/5"
                    >
                      Use Best Zone
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleApplyStrictnessPreview}
                      disabled={strictnessApplyBusy || !strictnessSpotlight}
                      className="h-8 px-3 text-[11px] bg-[#00f5d4]/20 text-[#00f5d4] border border-[#00f5d4]/30 hover:bg-[#00f5d4]/25"
                    >
                      {strictnessApplyBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                      {strictnessMatchesSelected ? 'Refresh Spotlight' : 'Bring To Front'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <div
            ref={chartCardRef}
            className={`relative ${isFullscreen ? '' : 'overflow-auto min-h-[420px] max-h-[1400px]'}`}
            style={isFullscreen ? undefined : { height: `${chartPanelHeight}px` }}
          >
            <Card className="bg-[#0e0f12] border-white/5 overflow-hidden h-full">
              <CardContent className="p-0 h-full">
                <div className="wk-chart-container relative flex h-full items-center justify-center overflow-hidden" data-testid="chart-container">
                  {chartView === 'original' ? (
                    analysis?.source_type === 'image_upload' ? (
                      <img
                        ref={chartImageRef}
                        key={`original-${selectedCandidateIdx}`}
                        src={getChartUrl('original')}
                        alt="Original Chart"
                        className={chartImageClass}
                        data-testid="chart-image-original"
                        onLoad={syncImageSize}
                        onError={(event) => { event.target.style.display = 'none'; }}
                      />
                    ) : previewUrl ? (
                      <img
                        ref={chartImageRef}
                        src={previewUrl}
                        alt="Original Chart"
                        className={chartImageClass}
                        onLoad={syncImageSize}
                      />
                    ) : (
                      <div className="flex items-center justify-center h-[400px] text-[#a1a1aa] text-sm">
                        Original image not available for this analysis
                      </div>
                    )
                  ) : (
                    <img
                      ref={chartImageRef}
                      key={`${chartView}-${selectedCandidateIdx}-${showFibs}-${showTimeframe}-${predictionSteps}`}
                      src={getChartUrl(chartView)}
                      alt={chartView === 'annotated' ? 'Annotated Chart' : 'Clean Replot'}
                      className={chartImageClass}
                      data-testid="chart-image"
                      onLoad={syncImageSize}
                      onError={(event) => { event.target.style.display = 'none'; }}
                    />
                  )}

                  {hasImageOverlay && (
                    <div className="absolute z-10" style={imageOverlayStyle}>
                      {selectedCandidate && chartMetadata?.points?.length > 0 && chartView !== 'clean' && (
                        <DraggablePivotLayer
                          points={chartMetadata.points}
                          chartMeta={chartMetadata.chart_meta}
                          imageWidth={imageSize.width}
                          imageHeight={imageSize.height}
                          disabled={rescoreBusy || doodleMode}
                          draftPivots={pivotDrafts}
                          onDraftChange={setPivotDrafts}
                          onDraftCommit={handlePivotDraftCommit}
                          snapEnabled={pivotSnapEnabled}
                          showGridDuringDrag={pivotSnapEnabled}
                        />
                      )}

                      {chartView !== 'clean' && (
                        <div
                          className={`absolute inset-0 ${spanSelectMode ? 'cursor-crosshair pointer-events-auto' : 'pointer-events-none'}`}
                          onPointerDown={handleSpanPointerDown}
                          onPointerMove={handleSpanPointerMove}
                          onPointerUp={handleSpanPointerUp}
                          onPointerCancel={handleSpanPointerUp}
                        >
                          <div
                            className="absolute inset-y-0 bg-[#00f5d4]/12 border-x border-[#00f5d4]/45"
                            style={{
                              left: `${activeSpan.start_pct}%`,
                              width: `${Math.max(1, activeSpan.end_pct - activeSpan.start_pct)}%`,
                            }}
                          />
                          {spanSelectMode && (
                            <div className="absolute top-3 left-3 px-2 py-1 rounded-sm bg-black/65 border border-[#00f5d4]/30 text-[10px] text-[#00f5d4]">
                              Drag to select macro range
                            </div>
                          )}
                        </div>
                      )}

                      {doodleMode && chartView !== 'clean' && (
                        <DoodleCanvas
                          ref={doodleCanvasRef}
                          width={imageSize.width}
                          height={imageSize.height}
                          enabled={doodleMode}
                          onInkChange={(nextHasInk) => {
                            setDoodleHasInk(nextHasInk);
                            setDoodleHistory((prev) => ({ ...prev, hasInk: nextHasInk }));
                          }}
                          onHistoryChange={setDoodleHistory}
                        />
                      )}
                    </div>
                  )}

                  {selectedCandidate && (
                    <div className="absolute left-3 top-3 flex flex-wrap items-center gap-2 max-w-[calc(100%-24px)]">
                      <div className="glass-panel rounded-sm px-3 py-1.5 flex items-center gap-2 border border-white/10 bg-black/45">
                        <Activity className="w-3 h-3 text-[#f5b400]" />
                        <span className="text-xs font-mono text-white uppercase">{String(selectedCandidate.pattern_type || 'unknown')}</span>
                        <span className="text-[10px] text-[#a1a1aa]">|</span>
                        <span className="text-xs font-mono text-white">{selectedCandidate.confidence?.toFixed(1)}%</span>
                      </div>
                      {activePredictedPivots.length > 0 && (
                        <div className="glass-panel rounded-sm px-3 py-1.5 flex items-center gap-2 border border-[#00f5d4]/30 bg-black/45">
                          <Sparkles className="w-3 h-3 text-[#00f5d4]" />
                          <span className="text-xs font-mono text-[#00f5d4]">+{activePredictedPivots.length} Predictions</span>
                          <span className="text-[10px] text-[#a1a1aa]">|</span>
                          <span className="text-xs font-mono text-white">{predictions?.prediction_confidence?.toFixed(1)}%</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            {!isFullscreen && (
              <div className="pt-2 flex justify-center">
                <button
                  type="button"
                  onPointerDown={beginChartResize}
                  className={`h-5 w-36 rounded-full border font-mono text-[9px] transition-colors ${isChartResizing ? 'border-[#00f5d4]/60 bg-[#00f5d4]/15 text-[#00f5d4]' : 'border-white/10 bg-white/5 text-[#6b7280] hover:border-[#00f5d4]/35 hover:text-[#00f5d4]'} cursor-row-resize`}
                  title="Drag to resize chart height"
                >
                  drag chart height
                </button>
              </div>
            )}
          </div>

          {lastRescoreResult && (
            <Card className="bg-[#14161a] border-[#00f5d4]/20" data-testid="rescore-delta-panel">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-white">Pivot Rescore Delta</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="p-2 rounded-sm bg-white/[0.02] border border-white/5">
                    <p className="text-[#a1a1aa]">Before</p>
                    <p className="font-mono text-white font-bold">{(lastRescoreResult.original_confidence ?? 0).toFixed(1)}%</p>
                  </div>
                  <div className="p-2 rounded-sm bg-white/[0.02] border border-white/5">
                    <p className="text-[#a1a1aa]">After</p>
                    <p className="font-mono text-white font-bold">{(lastRescoreResult.new_confidence ?? 0).toFixed(1)}%</p>
                  </div>
                  <div className="p-2 rounded-sm bg-white/[0.02] border border-white/5">
                    <p className="text-[#a1a1aa]">Delta</p>
                    <p className={`font-mono font-bold ${(lastRescoreResult.delta ?? 0) >= 0 ? 'text-[#00f5d4]' : 'text-[#ff3b30]'}`}>
                      {(lastRescoreResult.delta ?? 0) >= 0 ? '+' : ''}{(lastRescoreResult.delta ?? 0).toFixed(2)}%
                    </p>
                  </div>
                </div>
                {Array.isArray(lastRescoreResult.rule_changes) && lastRescoreResult.rule_changes.length > 0 && (
                  <div className="space-y-1">
                    {lastRescoreResult.rule_changes.slice(0, 6).map((rule, idx) => (
                      <p key={idx} className="text-[11px] text-[#a1a1aa]">{rule}</p>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {fractalEnabled && (
            <Card className="bg-[#14161a] border-white/5" data-testid="fractal-panel">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-sm font-medium text-white flex items-center gap-2">
                    <GitBranch className="w-4 h-4 text-[#00f5d4]" /> Fractal Explorer
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    {fractalPath?.length > 0 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={handleFractalBack}
                        disabled={fractalLoading}
                        className="h-7 px-2 text-[10px]"
                      >
                        <ChevronLeft className="w-3 h-3 mr-1" /> Back
                      </Button>
                    )}
                    {fractalLoading && <Loader2 className="w-3 h-3 animate-spin text-[#00f5d4]" />}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {fractalView ? (
                  <>
                    <p className="text-[11px] text-[#a1a1aa]">{fractalView.narrative}</p>
                    {Array.isArray(fractalView.breadcrumb) && fractalView.breadcrumb.length > 0 && (
                      <p className="text-[10px] text-[#a1a1aa]/80 font-mono">{fractalView.breadcrumb.join(' → ')}</p>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {(fractalView.waves || []).map((wave, idx) => (
                        <button
                          key={`${wave.wave_label}-${idx}`}
                          onClick={() => handleFractalZoom?.(idx)}
                          disabled={fractalLoading || !wave.has_sub_structure}
                          className={`text-left p-2 rounded-sm border transition-colors ${wave.has_sub_structure
                              ? 'border-[#00f5d4]/20 bg-[#00f5d4]/5 hover:bg-[#00f5d4]/10'
                              : 'border-white/10 bg-white/[0.02] opacity-70 cursor-not-allowed'
                            }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-mono text-sm text-white">{wave.wave_label}</span>
                            <span className="text-[10px] text-[#a1a1aa]">{wave.degree}</span>
                          </div>
                          <p className="text-[10px] text-[#a1a1aa] mt-1">{wave.verification_status} • {wave.confidence?.toFixed?.(1) ?? wave.confidence}%</p>
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="text-xs text-[#a1a1aa]">Fractal data is unavailable for this candidate.</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Predictions Panel */}
          {activePredictedPivots.length > 0 && (
            <Card className="bg-[#14161a] border-[#00f5d4]/20" data-testid="predictions-panel">
              <button
                className="w-full flex items-center justify-between p-3 text-left"
                onClick={() => setExpandedPredictions(!expandedPredictions)}
                data-testid="predictions-toggle"
              >
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#00f5d4]" />
                  <span className="text-sm font-medium text-[#00f5d4]">
                    Future Wave Predictions ({activePredictedPivots.length})
                  </span>
                  <Badge className="bg-[#00f5d4]/10 text-[#00f5d4] border-[#00f5d4]/20 text-[9px]">
                    {predictionBiasLabel} � {predictions.prediction_confidence?.toFixed(0)}%
                  </Badge>
                </div>
                {expandedPredictions ? <ChevronUp className="w-4 h-4 text-[#a1a1aa]" /> : <ChevronDown className="w-4 h-4 text-[#a1a1aa]" />}
              </button>
              {expandedPredictions && (
                <CardContent className="pt-0 space-y-3">
                  {hasCompletionPredictions && predictions?.predicted_pivots?.length > completionPredictions.length && (
                    <div className="flex items-center gap-1 rounded-sm border border-white/10 p-1 w-fit">
                      <button
                        type="button"
                        onClick={() => setPredictionTab('completion')}
                        className={`px-2 py-1 text-[10px] rounded-sm ${predictionTab === 'completion' ? 'bg-[#00f5d4]/15 text-[#00f5d4]' : 'text-[#a1a1aa]'}`}
                      >
                        Structure Completion
                      </button>
                      <button
                        type="button"
                        onClick={() => setPredictionTab('speculative')}
                        className={`px-2 py-1 text-[10px] rounded-sm ${predictionTab === 'speculative' ? 'bg-[#00f5d4]/15 text-[#00f5d4]' : 'text-[#a1a1aa]'}`}
                      >
                        Speculative Extension
                      </button>
                    </div>
                  )}
                  <div className="space-y-2">
                    {activePredictedPivots.map((pred, i) => (
                      <div key={i} className="p-2.5 rounded-sm bg-[#00f5d4]/5 border border-[#00f5d4]/10">
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2">
                            <span className="w-6 h-6 flex items-center justify-center rounded-sm bg-[#00f5d4]/20 text-[#00f5d4] text-xs font-mono font-bold">
                              {pred.label}*
                            </span>
                            <span className="text-xs text-white font-medium">Wave {pred.label}</span>
                            <Badge className="bg-[#00f5d4]/10 text-[#00f5d4] border-0 text-[9px]">
                              {pred.confidence?.toFixed(0)}%
                            </Badge>
                          </div>
                          <div className="flex items-center gap-3 font-mono text-xs">
                            <span className="text-[#a1a1aa]">Low: <span className="text-white">{pred.price_low?.toFixed(2)}</span></span>
                            <span className="text-[#00f5d4] font-bold">Mid: {pred.price_mid?.toFixed(2)}</span>
                            <span className="text-[#a1a1aa]">High: <span className="text-white">{pred.price_high?.toFixed(2)}</span></span>
                          </div>
                        </div>
                        <p className="text-[10px] text-[#a1a1aa] italic">{pred.rationale}</p>
                        <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                          {pred.methods_used?.map((m, j) => (
                            <span key={j} className="px-1.5 py-0.5 rounded-full bg-white/5 text-[9px] text-[#a1a1aa]">
                              {m.replace(/_/g, ' ')}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  {predictions.validation_results?.length > 0 && (
                    <div className="border-t border-white/5 pt-3">
                      <p className="text-xs text-[#a1a1aa] font-medium mb-2">Validation Checks</p>
                      <div className="space-y-1">
                        {predictions.validation_results.map((v, i) => (
                          <div key={i} className="flex items-center gap-2">
                            {v.passed ? (
                              <CheckCircle2 className="w-3 h-3 text-[#00f5d4] flex-shrink-0" />
                            ) : (
                              <XCircle className="w-3 h-3 text-[#ff3b30] flex-shrink-0" />
                            )}
                            <span className="text-[10px] text-[#a1a1aa]">{v.description}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <p className="text-[10px] text-[#a1a1aa]/50 text-right">
                    Computed in {predictions.iteration_count} iteration(s)
                  </p>
                </CardContent>
              )}
            </Card>
          )}

          {/* Explain Section */}
          {selectedCandidate && (
            <Card className="bg-[#14161a] border-white/5">
              <button
                className="w-full flex items-center justify-between p-3 text-left"
                onClick={() => setExpandedExplain(!expandedExplain)}
                data-testid="explain-toggle"
              >
                <span className="text-sm font-medium text-[#f5b400]">
                  {expandedExplain ? 'Hide' : 'Explain in Detail'}
                </span>
                {expandedExplain ? <ChevronUp className="w-4 h-4 text-[#a1a1aa]" /> : <ChevronDown className="w-4 h-4 text-[#a1a1aa]" />}
              </button>
              {expandedExplain && (
                <CardContent className="pt-0 space-y-3">
                  <div className="text-xs text-[#a1a1aa] leading-relaxed space-y-2">
                    <p>
                      <span className="text-white font-medium">Pattern:</span>{' '}
                      {selectedCandidate.pattern_type === 'impulse'
                        ? 'This appears to be a 5-wave impulse structure (0 → 1 → 2 → 3 → 4 → 5), suggesting a trending move.'
                        : 'This appears to be an A-B-C corrective structure (0 → A → B → C), suggesting a counter-trend move.'}
                    </p>
                    <p>
                      <span className="text-white font-medium">Pivot Sequence:</span>{' '}
                      {selectedCandidate.pivots?.map(p => `${p.label}=${typeof p.price === 'number' ? p.price.toFixed(1) : p.price}`).join(' → ')}
                    </p>
                    <div>
                      <span className="text-white font-medium">Rule Validation:</span>
                      <ul className="mt-1 space-y-0.5 ml-4">
                        {selectedCandidate.rule_checks?.map((r, i) => (
                          <li key={i} className="flex items-center gap-1.5">
                            {r.passed ? <CheckCircle2 className="w-3 h-3 text-[#00f5d4] flex-shrink-0" /> : <XCircle className="w-3 h-3 text-[#ff3b30] flex-shrink-0" />}
                            <span>{r.rule_name || r.description}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    {selectedCandidate.fib_relations?.length > 0 && (
                      <div>
                        <span className="text-white font-medium">Fibonacci Confluence:</span>
                        <ul className="mt-1 space-y-0.5 ml-4">
                          {selectedCandidate.fib_relations.map((f, i) => {
                            const relationship = f.wave || ((f.wave_from && f.wave_to) ? `${f.wave_from} → ${f.wave_to}` : '—');
                            const ratioVal = (typeof f.actual === 'number') ? f.actual : f.ratio;
                            const ratioTxt = (typeof ratioVal === 'number') ? ratioVal.toFixed(3) : '—';
                            const passed = (f.within_range ?? f.passed);

                            return (
                              <li key={i}>
                                {relationship}: <span className="font-mono">{ratioTxt}</span>
                                {' '}{passed ? <span className="text-[#00f5d4]">(in range)</span> : <span className="text-[#ff3b30]">(out of range)</span>}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                    <p>
                      <span className="text-white font-medium">Confidence Breakdown:</span>{' '}
                      Rules: {selectedCandidate.confidence_breakdown?.rule_satisfaction?.toFixed(1)}/40 |
                      Fibs: {selectedCandidate.confidence_breakdown?.fib_confluence?.toFixed(1)}/40 |
                      Simplicity: {selectedCandidate.confidence_breakdown?.simplicity?.toFixed(1)}/20
                    </p>
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* Timeframe Prediction Panel */}
          {showTimeframe && timeframeMethods?.length > 0 && (
            <Card className="bg-[#14161a] border-white/5">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-white flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#00f5d4]" /> Timeframe Estimation
                  <Badge className="bg-[#f5b400]/10 text-[#f5b400] border-[#f5b400]/20 text-[9px]">SPECULATIVE</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex gap-1 overflow-x-auto pb-2" data-testid="timeframe-methods">
                    {timeframeMethods.map((m, i) => (
                      <button
                        key={i}
                        onClick={() => setSelectedMethod(i)}
                        className={`flex-shrink-0 px-3 py-1.5 rounded-sm text-[11px] transition-colors ${selectedMethod === i ? 'bg-[#00f5d4]/10 text-[#00f5d4] border border-[#00f5d4]/20' : 'text-[#a1a1aa] hover:text-white bg-white/[0.02]'
                          }`}
                      >
                        {m.method || m.method_id}
                      </button>
                    ))}
                  </div>
                  {timeframeMethods[selectedMethod] && (
                    <div className="p-3 rounded-sm bg-white/[0.02] border border-white/5">
                      <div className="grid grid-cols-3 gap-4 text-center mb-3">
                        <div>
                          <p className="text-xs text-[#a1a1aa] uppercase tracking-wider mb-1">Optimistic</p>
                          <p className="font-mono text-lg text-[#00f5d4] font-bold">{timeframeMethods[selectedMethod].eta_low}</p>
                        </div>
                        <div>
                          <p className="text-xs text-[#a1a1aa] uppercase tracking-wider mb-1">Mid Estimate</p>
                          <p className="font-mono text-lg text-[#f5b400] font-bold">{timeframeMethods[selectedMethod].eta_mid}</p>
                        </div>
                        <div>
                          <p className="text-xs text-[#a1a1aa] uppercase tracking-wider mb-1">Conservative</p>
                          <p className="font-mono text-lg text-[#ff3b30] font-bold">{timeframeMethods[selectedMethod].eta_high}</p>
                        </div>
                      </div>
                      <p className="text-xs text-[#a1a1aa] italic">{timeframeMethods[selectedMethod].rationale}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Fib Table */}
          {selectedCandidate?.fib_relations?.length > 0 && (
            <Card className="bg-[#14161a] border-white/5">
              <button className="w-full flex items-center justify-between p-3" onClick={() => setExpandedFibs(!expandedFibs)}>
                <span className="text-sm font-medium text-white">Fibonacci Relationships</span>
                {expandedFibs ? <ChevronUp className="w-4 h-4 text-[#a1a1aa]" /> : <ChevronDown className="w-4 h-4 text-[#a1a1aa]" />}
              </button>
              {expandedFibs && (
                <CardContent className="pt-0">
                  <table className="w-full text-xs" data-testid="fib-table">
                    <thead>
                      <tr className="border-b border-white/5">
                        <th className="text-left py-2 text-[10px] uppercase tracking-wider text-[#a1a1aa] font-bold">Relationship</th>
                        <th className="text-right py-2 text-[10px] uppercase tracking-wider text-[#a1a1aa] font-bold">Ratio</th>
                        <th className="text-right py-2 text-[10px] uppercase tracking-wider text-[#a1a1aa] font-bold">Expected</th>
                        <th className="text-center py-2 text-[10px] uppercase tracking-wider text-[#a1a1aa] font-bold">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedCandidate.fib_relations.map((f, i) => {
                        const relationship = f.wave || ((f.wave_from && f.wave_to) ? `${f.wave_from} → ${f.wave_to}` : '');
                        const ratioVal = (typeof f.actual === 'number') ? f.actual : f.ratio;
                        const ratioTxt = (typeof ratioVal === 'number') ? ratioVal.toFixed(3) : '—';
                        const expectedTxt = f.expected || (Array.isArray(f.expected_range) ? f.expected_range.join(' – ') : '');
                        const passed = (f.within_range ?? f.passed);

                        return (
                          <tr key={i} className="border-b border-white/5 last:border-0">
                            <td className="py-2 text-white">{relationship}</td>
                            <td className="py-2 text-right font-mono text-white">{ratioTxt}</td>
                            <td className="py-2 text-right font-mono text-[#a1a1aa]">{expectedTxt}</td>
                            <td className="py-2 text-center">
                              <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${passed ? 'wk-badge-pass' : 'wk-badge-fail'}`}>
                                {passed ? 'PASS' : 'MISS'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </CardContent>
              )}
            </Card>
          )}
        </div>

        {/* Right: Candidates + Pivots */}
        <div className="space-y-3">
          <Card className="bg-[#14161a] border-white/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-white flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#00f5d4]" /> Saved Analyses
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="max-h-[220px]">
                <div className="divide-y divide-white/5" data-testid="analysis-history">
                  {historyItems.length === 0 && (
                    <div className="p-3 text-[11px] text-[#6b7280]">No saved analyses yet.</div>
                  )}
                  {historyItems.map((entry, idx) => {
                    const isActive = analysis?.id === entry.id;
                    const timestamp = entry?.last_rescored_at || entry?.created_at;
                    const sourceLabel = entry?.metadata?.upload_source || entry?.source_type || 'analysis';
                    const candidateCount = Array.isArray(entry?.candidates) ? entry.candidates.length : 0;
                    const cacheReuseCount = Number(entry?.metadata?.cache_reuse_count || 0);
                    const lastCacheHitAt = entry?.metadata?.cache_last_hit_at;

                    return (
                      <button
                        key={entry.id || idx}
                        type="button"
                        onClick={() => onSelectAnalysis?.(entry.id)}
                        className={`w-full text-left p-3 transition-colors ${isActive ? 'bg-[#00f5d4]/7 border-l-2 border-[#00f5d4]' : 'hover:bg-white/5'}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-[11px] font-mono text-white">{entry.id?.slice(0, 8)}</span>
                              {entry?.metadata?.cache_key ? (
                                <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-[#f5b400]/10 text-[#f5b400] border border-[#f5b400]/20">CACHED</span>
                              ) : null}
                              {entry?.last_rescored_at ? (
                                <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-[#00f5d4]/10 text-[#00f5d4] border border-[#00f5d4]/20">EDITED</span>
                              ) : null}
                            </div>
                            <div className="mt-1 text-[10px] text-[#a1a1aa] font-mono">
                              {String(sourceLabel)} • {candidateCount} candidate{candidateCount === 1 ? '' : 's'}
                              {cacheReuseCount > 0 ? ` • reused ${cacheReuseCount}x` : ''}
                            </div>
                            {lastCacheHitAt ? (
                              <div className="mt-1 text-[10px] text-[#6b7280] font-mono">
                                last cache hit {new Date(lastCacheHitAt).toLocaleString()}
                              </div>
                            ) : null}
                          </div>
                          <div className="text-right text-[10px] text-[#6b7280]">
                            {timestamp ? new Date(timestamp).toLocaleString() : '—'}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Candidates List */}
          <Card className="bg-[#14161a] border-white/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-white flex items-center gap-2">
                <Activity className="w-4 h-4 text-[#f5b400]" /> Candidates
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="max-h-[250px]">
                <div className="divide-y divide-white/5" data-testid="candidates-list">
                  {analysis.candidates?.map((c, idx) => (
                    <div
                      key={c.id}
                      data-testid={`candidate-${idx}`}
                      onClick={() => { setSelectedCandidate(c); setSelectedCandidateIdx(idx); }}
                      className={`flex items-center justify-between p-3 cursor-pointer transition-colors ${selectedCandidate?.id === c.id ? 'bg-[#f5b400]/5 border-l-2 border-[#f5b400]' : 'hover:bg-white/5'
                        }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono font-bold text-white uppercase">{c.pattern_type}</span>
                          {c.preferred && <Star className="w-3 h-3 text-[#f5b400] fill-[#f5b400]" />}
                        </div>
                        <span className="text-[10px] font-mono text-[#a1a1aa]">{c.labels?.join(' → ')}</span>
                      </div>
                      <div className="text-right ml-3">
                        <div className="font-mono text-sm font-bold text-white">{c.confidence?.toFixed(1)}%</div>
                        <div className="wk-confidence-bar w-16 mt-1"><div className="wk-confidence-fill" style={{ width: `${c.confidence}%` }} /></div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {strictnessInterval && (
            <Card className="border-[#00f5d4]/15 bg-[radial-gradient(circle_at_top_left,_rgba(0,245,212,0.16),_transparent_48%),linear-gradient(180deg,_rgba(20,22,26,0.98),_rgba(11,13,16,0.98))]" data-testid="strictness-sidebar-card">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <CardTitle className="text-sm font-medium text-white flex items-center gap-2">
                      <SlidersHorizontal className="w-4 h-4 text-[#00f5d4]" /> Strictness Preview
                    </CardTitle>
                    <p className="mt-1 text-[11px] text-[#8b93a7]">
                      Preview only. It shows which count would lead at this slider setting. Your live count stays put until you click Make Live Count.
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1 text-[10px] font-mono">
                    <span className="rounded-full border border-[#f5b400]/20 bg-[#f5b400]/10 px-2 py-0.5 text-[#f5b400]">
                      {strictnessToneLabel}
                    </span>
                    <span className="rounded-full border border-white/10 bg-white/[0.04] px-2 py-0.5 text-[#a1a1aa]">
                      Best Range {(strictnessInterval.best_interval?.low ?? 0).toFixed(2)} - {(strictnessInterval.best_interval?.high ?? 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-2 text-[10px]">
                  <div className="rounded-sm border border-white/8 bg-black/20 p-2.5">
                    <p className="uppercase tracking-[0.22em] text-[#6b7280]">Probe</p>
                    <p className="mt-1 font-mono text-lg font-bold text-[#f5b400]">{(strictnessProbe ?? 0).toFixed(2)}</p>
                  </div>
                  <div className="rounded-sm border border-white/8 bg-black/20 p-2.5">
                    <p className="uppercase tracking-[0.22em] text-[#6b7280]">Preview Leader</p>
                    <p className="mt-1 text-sm font-bold uppercase text-white">{String(strictnessSpotlight?.pattern_type || 'pending')}</p>
                    <p className="font-mono text-[11px] text-[#00f5d4]">{(strictnessSpotlight?.confidence ?? 0).toFixed(1)}%</p>
                  </div>
                </div>

                <div className="rounded-xl border border-white/8 bg-black/20 p-3">
                  <div className="mb-2 flex items-center justify-between text-[10px] uppercase tracking-[0.22em] text-[#6b7280]">
                    <span>Loose</span>
                    <span>Exact</span>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={1}
                    value={Math.round((strictnessProbe ?? 0) * 100)}
                    onChange={(event) => setStrictnessProbe?.(Number(event.target.value) / 100)}
                    className="w-full accent-[#f5b400]"
                  />
                  <div className="mt-2 flex items-center justify-between text-[10px] text-[#8b93a7]">
                    <span className="font-mono">Best range {(strictnessInterval.best_interval?.low ?? 0).toFixed(2)} - {(strictnessInterval.best_interval?.high ?? 0).toFixed(2)}</span>
                    <span className={`font-mono ${strictnessMatchesSelected ? 'text-[#00f5d4]' : 'text-[#a1a1aa]'}`}>
                      {strictnessMatchesSelected ? 'Live count already matches preview' : 'Preview only'}
                    </span>
                  </div>
                </div>

                <div className="rounded-xl border border-[#00f5d4]/12 bg-[#00f5d4]/[0.05] p-3">
                  {strictnessPreviewLoading ? (
                    <div className="flex items-center gap-2 text-[11px] text-[#8b93a7]">
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-[#00f5d4]" /> Refreshing spotlight preview...
                    </div>
                  ) : strictnessSpotlight ? (
                    <div className="space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-[10px] uppercase tracking-[0.24em] text-[#8b93a7]">Preview Leader</p>
                          <p className="mt-1 text-lg font-semibold uppercase text-white">{String(strictnessSpotlight.pattern_type || 'unknown')}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-[10px] uppercase tracking-[0.24em] text-[#8b93a7]">Confidence</p>
                          <p className="font-mono text-xl font-bold text-[#00f5d4]">{(strictnessSpotlight.confidence ?? 0).toFixed(1)}%</p>
                        </div>
                      </div>
                      {(strictnessSpotlight.labels || []).length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {(strictnessSpotlight.labels || []).map((label, idx) => (
                            <span key={`${label}-${idx}`} className="rounded-full border border-white/10 bg-black/25 px-2 py-0.5 text-[10px] font-mono text-white">
                              {label}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center justify-between text-[10px] text-[#8b93a7]">
                        <span>Closest sample: <span className="font-mono text-white uppercase">{String(strictnessSample?.pattern_type || 'pending')}</span></span>
                        <span className="font-mono text-[#a1a1aa]">{strictnessPreviewCandidates.length} preview</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setStrictnessDetailOpen((value) => !value)}
                        className="flex w-full items-center justify-between rounded-sm border border-white/10 bg-black/20 px-2 py-1.5 text-[10px] uppercase tracking-[0.22em] text-[#a1a1aa] hover:text-white"
                      >
                        <span>Why this count?</span>
                        {strictnessDetailOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                      </button>
                      {strictnessDetailOpen && (
                        <p className="text-[11px] leading-relaxed text-[#d4d7dd]">
                          {strictnessSpotlight.rationale || 'This preview surfaced the cleanest interpretation from the current pivot chain. It will not replace the live count until you apply it.'}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-[11px] text-[#8b93a7]">No alternate counts surfaced for this probe yet.</p>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.22em] text-[#6b7280]">
                    <span>Preview Stack</span>
                    <span className="font-mono normal-case tracking-normal text-[#a1a1aa]">{strictnessPreviewCandidates.length} candidates</span>
                  </div>
                  {strictnessPreviewCandidates.slice(0, 3).map((candidate, idx) => (
                    <div key={`strictness-sidebar-preview-${idx}`} className={`rounded-sm border px-2.5 py-2 ${idx === 0 ? 'border-[#00f5d4]/25 bg-[#00f5d4]/10' : 'border-white/8 bg-white/[0.03]'}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[11px] font-semibold uppercase text-white">{String(candidate.pattern_type || 'unknown')}</span>
                        <span className="font-mono text-[10px] text-[#f5b400]">{(candidate.confidence ?? 0).toFixed(1)}%</span>
                      </div>
                      <p className="mt-1 text-[10px] font-mono text-[#8b93a7]">{(candidate.labels || []).join(' → ') || 'No labels'}</p>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                  onClick={() => setStrictnessProbe?.(strictnessInterval.best?.strictness ?? strictnessProbe)}
                  className="h-8 px-2 text-[10px] border-white/10 bg-[#14161a] hover:bg-white/5"
                >
                    Jump To Best Range
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleRefreshStrictnessPreview}
                    disabled={strictnessPreviewLoading}
                    className="h-8 px-2 text-[10px] border-white/10 bg-[#14161a] hover:bg-white/5"
                  >
                    {strictnessPreviewLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RotateCcw className="w-3 h-3 mr-1" />}
                    Refresh Preview
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleApplyStrictnessPreview}
                    disabled={strictnessApplyBusy || !strictnessSpotlight}
                    className="h-8 px-2 text-[10px] bg-[#00f5d4]/20 text-[#00f5d4] border border-[#00f5d4]/30 hover:bg-[#00f5d4]/25"
                  >
                    {strictnessApplyBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                    Make Live Count
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleRestoreStrictnessStack}
                    disabled={!strictnessRestoreAvailable || strictnessApplyBusy}
                    className="h-8 px-2 text-[10px] border-[#f5b400]/30 bg-[#f5b400]/10 text-[#f5b400] hover:bg-[#f5b400]/15"
                  >
                    <RotateCcw className="w-3 h-3 mr-1" /> Restore Previous Count
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Confidence Ledger — backend weights only; never client-invented deltas */}
          {selectedCandidate && (
            <Card className="bg-[#14161a] border-white/5">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-white">Confidence Ledger</CardTitle>
                {ledgerHasSynthetic ? (
                  <p className="text-[10px] text-amber-400/90 mt-1" data-testid="ledger-synthetic-banner">
                    Some rows are approximate / estimate — not backend scoring weights.
                  </p>
                ) : null}
              </CardHeader>
              <CardContent>
                <div className="space-y-1.5" data-testid="rule-checks">
                  {ledgerRows.length === 0 ? (
                    <p className="text-[11px] text-[#6b7280]" data-testid="ledger-empty">
                      No backend ledger for this candidate yet. Confidence deltas are not estimated client-side.
                    </p>
                  ) : null}
                  {ledgerRows.map((entry, i) => {
                    const isSynthetic = Boolean(entry?.synthetic);
                    const hasNumericDelta = entry?.delta !== null && entry?.delta !== undefined && !Number.isNaN(Number(entry.delta));
                    const delta = hasNumericDelta ? Number(entry.delta) : null;
                    const kind = String(entry?.kind || (delta !== null && delta > 0 ? 'bonus' : delta !== null && delta < 0 ? 'penalty' : 'neutral')).toLowerCase();
                    const isFatal = kind === 'fatal';
                    const isBonus = kind === 'bonus';
                    const isPenalty = kind === 'penalty';
                    const title = entry?.check || entry?.rule_name || entry?.description || 'Ledger';
                    const detail = entry?.reason || entry?.detail || '';

                    return (
                      <div
                        key={i}
                        className={`flex items-start justify-between py-1.5 gap-2 ${isSynthetic ? 'opacity-80 border border-dashed border-amber-500/40 rounded px-1.5' : ''}`}
                        data-testid={isSynthetic ? 'ledger-row-synthetic' : 'ledger-row-real'}
                        data-synthetic={isSynthetic ? 'true' : 'false'}
                      >
                        <div className="flex items-start gap-2 flex-1 min-w-0">
                          {isFatal || isPenalty ? (
                            <XCircle className="w-3.5 h-3.5 text-[#ff3b30] mt-[2px] flex-shrink-0" />
                          ) : (
                            <CheckCircle2 className="w-3.5 h-3.5 text-[#00f5d4] mt-[2px] flex-shrink-0" />
                          )}
                          <div className="min-w-0">
                            <p className="text-[11px] text-[#a1a1aa] truncate">{title}</p>
                            {detail ? <p className="text-[10px] text-[#6b7280] truncate">{detail}</p> : null}
                            {isSynthetic ? (
                              <p className="text-[9px] uppercase tracking-wide text-amber-400/90" data-testid="ledger-approx-label">
                                approximate / estimate
                              </p>
                            ) : null}
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full ${isFatal ? 'wk-badge-fail' : isPenalty ? 'wk-badge-fail' : isBonus ? 'wk-badge-pass' : 'bg-white/10 text-[#a1a1aa]'
                            }`}>
                            {isSynthetic ? 'EST.' : isFatal ? 'FATAL' : isPenalty ? 'PENALTY' : isBonus ? 'BONUS' : 'NEUTRAL'}
                          </span>
                          <span
                            className={`text-[10px] font-mono ${delta !== null && delta > 0 ? 'text-[#00f5d4]' : delta !== null && delta < 0 ? 'text-[#ff3b30]' : 'text-[#a1a1aa]'}`}
                            data-testid="ledger-delta"
                          >
                            {delta === null
                              ? '—'
                              : delta > 0
                                ? `+${delta.toFixed(2)}`
                                : delta.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Pivots */}
          {selectedCandidate && (
            <Card className="bg-[#14161a] border-white/5">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium text-white">Pivots</CardTitle>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handlePrefer(selectedCandidate.id)}
                    className={`text-xs rounded-sm ${selectedCandidate.preferred ? 'text-[#f5b400]' : 'text-[#a1a1aa]'}`}
                    data-testid="prefer-btn"
                  >
                    <Star className={`w-3 h-3 mr-1 ${selectedCandidate.preferred ? 'fill-[#f5b400]' : ''}`} />
                    {selectedCandidate.preferred ? 'Preferred' : 'Prefer'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-[10px] text-[#a1a1aa] mb-2">Draft mode is active: drag pivots freely on X/Y. Apply/Rescore uses the exact draft coordinates; Snap Preview only affects the visual guide while dragging.</p>
                <div className="space-y-1" data-testid="pivots-list">
                  {selectedCandidate.pivots?.map((p, i) => {
                    const draft = pivotDrafts?.[i];
                    const draftPrice = draft ? mapSourceYToPrice?.(draft.sourceY) : null;
                    const displayPrice = Number.isFinite(draftPrice) ? draftPrice : p.price;

                    return (
                      <div key={i} className="flex items-center justify-between py-1 px-2 rounded-sm bg-white/[0.02]">
                        <span className="w-7 h-7 flex items-center justify-center rounded-sm bg-[#f5b400]/10 text-[#f5b400] text-[11px] font-mono font-bold">{p.label}</span>
                        <span className={`font-mono text-sm flex-1 text-center ${draft ? 'text-[#f5b400]' : 'text-white'}`}>{typeof displayPrice === 'number' ? displayPrice.toFixed(2) : displayPrice}</span>
                        <button className="p-1 rounded-sm hover:bg-white/5">
                          {p.locked ? <Lock className="w-3 h-3 text-[#f5b400]" /> : <Unlock className="w-3 h-3 text-[#a1a1aa]" />}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Analysis metadata */}
          <div className="text-xs text-[#a1a1aa]/50 font-mono relative">
            <div className="scanlines rounded-sm" />
            <div className="p-3 bg-[#14161a] border border-white/5 rounded-sm relative space-y-0.5">
              <p>ID: {analysis.id?.slice(0, 8)}</p>
              <p>Source: {analysis.source_type}</p>
              {analysis.processing_time_ms && <p>{analysis.processing_time_ms}ms</p>}
              {analysis.metadata?.ai_enhanced && <p className="text-[#f5b400]">AI Enhanced</p>}
            </div>
          </div>
        </div>
      </div>

      {!chatDetached && (
        <Card
          ref={chatPanelRef}
          className="bg-[#14161a] border-white/5 resize-y overflow-auto min-h-[260px] max-h-[760px]"
          style={{ height: `${inlineChatHeight}px` }}
          data-testid="chat-section"
        >
          {chatPanelContent}
        </Card>
      )}

      {chatDetached && (
        <Card
          ref={chatPanelRef}
          className="bg-[#14161a] border-white/10 fixed bottom-4 right-4 z-50 shadow-[0_20px_60px_rgba(0,0,0,0.6)] resize overflow-auto min-w-[360px] min-h-[280px] max-w-[900px] max-h-[860px]"
          style={{ width: `${floatingChatSize.width}px`, height: `${floatingChatSize.height}px` }}
          data-testid="chat-section-floating"
        >
          {chatPanelContent}
        </Card>
      )}
    </div>
  );
}





































